import { useMemo, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { Membership, Person, ServiceAttendance } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { formatDayHeading, formatTime } from '../calendar/dates';
import { useMe } from '../session/SessionProvider';
import { attendanceOpen } from './window';
import { QrCard } from './QrAttendance';

type MemberRow = Membership & { person: Person | null };

const fullName = (p: Person | null) => (p ? `${p.givenName} ${p.familyName}`.trim() : 'Persona');

/** Asistencia de un culto (Motor 9): conteo total y lista de quién asistió. */
export function AttendancePage() {
  const { serviceId = '' } = useParams();
  const me = useMe();
  const churchId = me.activeChurchId;
  const summary = useAsync(
    (signal) => (churchId ? api.get<ServiceAttendance>(`/attendance/churches/${churchId}/services/${serviceId}`, signal) : Promise.reject(new Error('Sin contexto'))),
    [churchId, serviceId],
  );
  const members = useAsync(
    (signal) =>
      churchId && me.can('members.read')
        ? api.get<{ items: MemberRow[] }>(`/multichurch/churches/${churchId}/memberships?status=active`, signal).then((r) => r.items)
        : Promise.resolve([]),
    [churchId],
  );

  if (!churchId) return <EmptyState title="Elige primero la iglesia en la que actúas." />;

  return (
    <div className="page">
      <p className="small">
        <Link to={`/app/cultos/${serviceId}`}>← Programa del culto</Link>
      </p>
      {summary.status === 'loading' ? <Loading /> : null}
      {summary.status === 'error' ? <ErrorState error={summary.error} onRetry={summary.reload} /> : null}
      {summary.status === 'ready' ? (
        <>
          <header className="page__header">
            <h1>Asistencia</h1>
            <p className="muted">
              {summary.data.occasion.title} · {formatDayHeading(new Date(summary.data.occasion.startsAt))} · {formatTime(summary.data.occasion.startsAt)}
            </p>
          </header>
          <div className="stat-row">
            <div className="stat">
              <span className="stat__value">{summary.data.headcount?.total ?? '—'}</span>
              <span className="stat__label">Conteo total</span>
            </div>
            <div className="stat">
              <span className="stat__value">{summary.data.headcount?.visitors ?? '—'}</span>
              <span className="stat__label">Visitas</span>
            </div>
            <div className="stat">
              <span className="stat__value">{summary.data.attendees.length}</span>
              <span className="stat__label">Por nombre</span>
            </div>
          </div>
          {attendanceOpen(summary.data.occasion) ? (
            <>
              {me.can('attendance.record') ? <HeadcountCard churchId={churchId} serviceId={serviceId} data={summary.data} onSaved={summary.reload} /> : null}
              <AttendeesCard churchId={churchId} serviceId={serviceId} data={summary.data} members={members.status === 'ready' ? members.data : []} onChange={summary.reload} />
            </>
          ) : (
            <Alert tone="info">{summary.data.occasion.status === 'canceled' ? 'Este culto fue cancelado.' : 'La asistencia se abre una hora antes del culto.'}</Alert>
          )}
          {me.can('attendance.record') && summary.data.occasion.status !== 'canceled' ? (
            <QrCard churchId={churchId} serviceId={serviceId} title={summary.data.occasion.title} />
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function HeadcountCard({ churchId, serviceId, data, onSaved }: { churchId: string; serviceId: string; data: ServiceAttendance; onSaved: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);
    try {
      await api.post(`/attendance/churches/${churchId}/services/${serviceId}/headcounts`, {
        total: Number(form.get('total')),
        visitors: Number(form.get('visitors') || 0),
      });
      onSaved();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Conteo">
      <form className="form" onSubmit={onSubmit} noValidate>
        {error ? <Alert>{error}</Alert> : null}
        <div className="form__row">
          <Field label="Total de personas" name="total" type="number" min={0} required defaultValue={data.headcount?.total ?? ''} />
          <Field label="Visitas" name="visitors" type="number" min={0} defaultValue={data.headcount?.visitors ?? 0} />
        </div>
        {data.headcount ? <p className="muted small">Guardar un nuevo conteo reemplaza el anterior; el anterior queda en el historial.</p> : null}
        <Button type="submit" variant="secondary" busy={busy}>
          Guardar conteo
        </Button>
      </form>
    </Card>
  );
}

function AttendeesCard({
  churchId,
  serviceId,
  data,
  members,
  onChange,
}: {
  churchId: string;
  serviceId: string;
  data: ServiceAttendance;
  members: MemberRow[];
  onChange: () => void;
}) {
  const [query, setQuery] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const present = useMemo(() => new Set(data.attendees.map((a) => a.personId)), [data.attendees]);

  // Miembros vigentes + quien ya registró (p. ej. invitados que marcaron "Estoy aquí").
  const rows = useMemo(() => {
    const byId = new Map<string, string>(members.map((m) => [m.person_id, fullName(m.person)]));
    for (const a of data.attendees) if (!byId.has(a.personId)) byId.set(a.personId, a.name);
    const q = query.trim().toLocaleLowerCase('es');
    return [...byId]
      .map(([personId, name]) => ({ personId, name }))
      .filter((r) => !q || r.name.toLocaleLowerCase('es').includes(q))
      .sort((a, b) => a.name.localeCompare(b.name, 'es'));
  }, [members, data.attendees, query]);

  async function toggle(personId: string) {
    setBusyId(personId);
    setError(null);
    const base = `/attendance/churches/${churchId}/services/${serviceId}/attendees`;
    try {
      if (present.has(personId)) await api.post(`${base}/${personId}/void`, {});
      else await api.post(base, { personId });
      onChange();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Card title="Quién asistió">
      {error ? <Alert>{error}</Alert> : null}
      <Field label="Buscar" name="q" type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Nombre" />
      {rows.length === 0 ? <EmptyState title={query ? 'Nadie coincide con la búsqueda.' : 'Aún no hay miembros.'} /> : null}
      <ul className="list">
        {rows.map((r) => {
          const attendee = data.attendees.find((a) => a.personId === r.personId);
          return (
            <li key={r.personId} className="list__item attendee">
              <div className="service__main">
                <strong>{r.name}</strong>
                {attendee ? <span className="muted small">{attendee.method === 'self' ? 'Registró su asistencia' : attendee.method === 'location' ? 'Registrado por ubicación' : 'Registrado por el equipo'} · {formatTime(attendee.recordedAt)}</span> : null}
              </div>
              <Button variant={attendee ? 'secondary' : 'ghost'} busy={busyId === r.personId} onClick={() => toggle(r.personId)} aria-pressed={Boolean(attendee)}>
                {attendee ? '✓ Asistió' : 'Marcar'}
              </Button>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
